import { useSelector } from 'react-redux'
import {
  selectFilteredSortedPlanGroupedProducts,
  selectSearch,
  selectSort,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.selectors.ts'
import { usePlanTableConfig } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/usePlanTableConfig.tsx'
import { Product } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PlanTableCell } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableCell'
import { PlanTableMonthHeader } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableMonthHeader'
import {
  SearchWrapper,
  TableLoader,
  Wrapper,
} from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableData.styles.ts'
import { CircularProgress } from '@mui/material'
import {
  DataGridPremium,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridColDef,
  gridColumnsTotalWidthSelector,
  GridRenderCellParams,
  GridRowHeightParams,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { PlanTableSearchHeader } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableSearchHeader'
import type { GridGroupingValueGetterParams } from '@mui/x-data-grid-premium/models'
import { PlanTableProductCell } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableProductCell'
import AutoSizer from 'react-virtualized-auto-sizer'
import {
  getGroupAndCategoryFromGroupingKey,
  getPlanGroupingKey,
} from '@/core/utils/replenishmentPlanGrouping.ts'
import { PlanTableMonthGroupCell } from '../PlanTableMonthGroupCell'
import { PlanTableTotalFooter } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableTotalFooter'
import { PlanTableMonthFooter } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableMonthFooter'
import PlanTableGroupingCell from '../PlanTableGroupingCell/PlanTableGroupingCell.tsx'
import { PlanTableDataPlainList } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableDataPlainList'
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium'
import { useShowScrollShadow } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableDataCategories/useShowScrollShadow.ts'
import { useTrackReplenishmentPlanAnalyticsEvent } from '@/core/hooks/useTrackReplenishmentPlanAnalyticsEvent.ts'
import {
  DatagridActionsEvent,
  DatagridActionsType,
} from '@/core/analytics/events/replenishmentPlan/DatagridActionsEvent.ts'
import { useTranslation } from 'react-i18next'
import { NothingFoundPlaceholder } from '@/ui/components/NothingFoundPlaceholder'
import { EmptyPlaceholderWrapper } from './PlanTableDataCategories.styles.tsx'

const PlanTableDataCategories = () => {
  const { isTableLoading } = usePlanTableConfig()

  const search = useSelector(selectSearch)

  if (search) {
    return <PlanTableDataPlainList />
  }

  return (
    <Wrapper>
      {isTableLoading && (
        <TableLoader>
          <CircularProgress />
        </TableLoader>
      )}

      <AutoSizer>
        {({ width, height }) => (
          <div style={{ width, height, position: 'relative' }}>
            <SearchWrapper>
              <PlanTableSearchHeader />
            </SearchWrapper>

            <PlanTableDataCategoriesAutoSized />
          </div>
        )}
      </AutoSizer>
    </Wrapper>
  )
}

const TOTAL_ROW_FOOTER_ID = 'total-row-footer'

const PlanTableDataCategoriesAutoSized = () => {
  const products = useSelector(selectFilteredSortedPlanGroupedProducts)

  const { trackReplenishmentPlanAnalyticsEvent } =
    useTrackReplenishmentPlanAnalyticsEvent()

  const sort = useSelector(selectSort)

  const apiRef = useGridApiRef()

  useEffect(() => {
    setTimeout(() => {
      apiRef.current?.scroll({ top: 0 })
    })
  }, [apiRef, sort])

  const { t } = useTranslation()

  const showShadow = useShowScrollShadow(apiRef)

  const { dates, formatted, isTableLoading } = usePlanTableConfig()

  const [expandedRowsIds, setExpandedRowsIds] = useState(new Set())

  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    return apiRef.current.subscribeEvent('scrollPositionChange', ({ left }) => {
      if (!rootRef.current) {
        return
      }

      if (left === 0) {
        rootRef.current.classList.add('hasScrollToLeft')
      } else {
        rootRef.current.classList.remove('hasScrollToLeft')
      }

      const columnsTotalWidth = gridColumnsTotalWidthSelector(apiRef)
      const dimensions = apiRef.current.getRootDimensions()

      if (
        Math.round(left + dimensions!.viewportOuterSize.width) >=
        columnsTotalWidth
      ) {
        rootRef.current.classList.add('hasScrollToRight')
      } else {
        rootRef.current.classList.remove('hasScrollToRight')
      }
    })
  }, [apiRef])

  const scrollToCurrentMonth = useCallback(() => {
    const columnsWidth = gridColumnsTotalWidthSelector(apiRef)

    // From here https://github.com/mui/mui-x/issues/6411#issuecomment-1271556519
    setTimeout(() => {
      apiRef.current?.scroll({
        top: 0,
        left: columnsWidth / 4,
      })
    })
  }, [apiRef])

  useEffect(() => {
    if (!isTableLoading) {
      scrollToCurrentMonth()
    }
  }, [isTableLoading, scrollToCurrentMonth])

  const initialState: GridInitialStatePremium = {
    columns: {
      columnVisibilityModel: {
        name: false,
      },
    },
    rowGrouping: {
      model: ['name'],
    },
    pinnedColumns: { left: [GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD] },
  }

  const onGroupingCellClick = useCallback(
    (rowId: string) => {
      trackReplenishmentPlanAnalyticsEvent(
        new DatagridActionsEvent({ type: DatagridActionsType.CategoryClick }),
      )

      const updatedExpandedRowsIds = new Set(expandedRowsIds)

      if (expandedRowsIds.has(rowId)) {
        updatedExpandedRowsIds.delete(rowId)
      } else {
        updatedExpandedRowsIds.add(rowId)
      }

      setExpandedRowsIds(updatedExpandedRowsIds)
    },
    [expandedRowsIds, trackReplenishmentPlanAnalyticsEvent],
  )

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        groupingValueGetter: (params: GridGroupingValueGetterParams<Product>) =>
          getPlanGroupingKey(params.row),
        renderHeader: () => <PlanTableSearchHeader />,
        renderCell: (params: GridRenderCellParams<Product>) => {
          if (params.rowNode.type === 'group') {
            const { groupCode, categoryCode } =
              getGroupAndCategoryFromGroupingKey(
                params.rowNode.groupingKey!.toString(),
              )

            const rowId = params.rowNode.id.toString()

            return (
              <PlanTableGroupingCell
                groupCode={groupCode}
                categoryCode={categoryCode}
                rowId={rowId}
                expanded={expandedRowsIds.has(rowId)}
                onClick={onGroupingCellClick}
              />
            )
          }

          if (params.id === TOTAL_ROW_FOOTER_ID) {
            return <PlanTableTotalFooter />
          }

          return <PlanTableProductCell product={params.row} />
        },
        width: 300,
      },
      ...dates.map<GridColDef>((date, index) => {
        const formattedMonth = formatted[index]

        return {
          field: formattedMonth,
          renderHeader: () => <PlanTableMonthHeader date={date} />,
          renderCell: (params: GridRenderCellParams<Product>) => {
            if (params.rowNode.type === 'group') {
              const { groupCode, categoryCode } =
                getGroupAndCategoryFromGroupingKey(
                  params.rowNode.groupingKey!.toString(),
                )

              return (
                <PlanTableMonthGroupCell
                  date={date}
                  groupCode={groupCode}
                  categoryCode={categoryCode}
                />
              )
            }

            if (params.id === TOTAL_ROW_FOOTER_ID) {
              return <PlanTableMonthFooter date={date} />
            }

            return <PlanTableCell product={params.row} date={date} />
          },
          width: 88,
        }
      }),
    ],
    [dates, expandedRowsIds, formatted, onGroupingCellClick],
  )

  return (
    <DataGridPremium
      ref={rootRef}
      disableColumnMenu
      hideFooterSelectedRowCount
      hideFooterRowCount
      hideFooter
      hideFooterPagination
      disableRowSelectionOnClick
      disableMultipleRowSelection
      disableColumnFilter
      disableMultipleColumnsSorting
      disableMultipleColumnsFiltering
      disableColumnResize
      disableColumnSelector
      disableColumnReorder
      defaultGroupingExpansionDepth={1}
      isGroupExpandedByDefault={(group) => expandedRowsIds.has(group.id)}
      apiRef={apiRef}
      initialState={initialState}
      rows={products}
      columns={columns}
      groupingColDef={{ leafField: 'name' }}
      getRowClassName={(params) => {
        if (
          !params.id.toString().includes('auto-generated-row-name') ||
          params.id === TOTAL_ROW_FOOTER_ID
        ) {
          return ''
        }

        if (expandedRowsIds.has(params.id)) {
          return ''
        }

        return params.indexRelativeToCurrentPage % 2 === 0
          ? ''
          : 'colorBackground'
      }}
      getRowHeight={(params: GridRowHeightParams) => {
        if (params.id.toString().includes('auto-generated-row-name')) {
          return 47
        }

        if (params.id === TOTAL_ROW_FOOTER_ID) {
          return 48
        }

        return 88
      }}
      slots={{
        groupingCriteriaExpandIcon: () => false,
        groupingCriteriaCollapseIcon: () => false,
        noRowsOverlay: () =>
          !isTableLoading && (
            <EmptyPlaceholderWrapper>
              <NothingFoundPlaceholder />
            </EmptyPlaceholderWrapper>
          ),
      }}
      pinnedRows={{
        bottom:
          products.length === 0
            ? []
            : [
                {
                  id: TOTAL_ROW_FOOTER_ID,
                },
              ],
      }}
      sx={{
        '& .MuiDataGrid-overlayWrapperInner': {
          position: 'relative',
        },
        '& .MuiDataGrid-columnHeaders ': {
          height: '48px !important',
          minHeight: '48px !important',
          borderBottom: '1px solid #F2F6FA',
        },
        '& .MuiDataGrid-cell': {
          padding: 0,
          whiteSpace: 'normal !important',
          borderBottom: '2px solid transparent',
        },
        '& .MuiDataGrid-iconButtonContainer': {
          display: 'none',
        },

        '& .MuiDataGrid-columnHeader': {
          padding: '0px',
          '&:focus': {
            outline: 'none',
          },
        },
        '& .MuiDataGrid-columnHeaderTitleContainerContent': {
          width: '100%',
        },
        '& .MuiDataGrid-groupingCriteriaCellToggle': {
          display: 'none',
        },
        '& .MuiDataGrid-groupingCriteriaCell': {
          '& > span': {
            display: 'none',
          },
        },
        '& .MuiDataGrid-row': {
          marginBottom: '0px',
          '&:hover': {
            backgroundColor: 'inherit',
          },
          '&.Mui-hovered': {
            backgroundColor: 'inherit',
          },
          '&.colorBackground': {
            position: 'relative',
            backgroundColor: (theme) => theme.palette.custom.gray4,
          },
        },
        '& .MuiDataGrid-cell:focus': {
          outline: 'none',
        },
        '& .MuiDataGrid-cell:focus-within': {
          outline: 'none',
        },
        '& .MuiDataGrid-pinnedColumns': {
          boxShadow: 'none',
          paddingRight: '3px',
        },
        '& .MuiDataGrid-pinnedColumnHeaders': {
          boxShadow: 'none',
        },
        '& .MuiDataGrid-pinnedRows': {
          outline: '1px solid #F2F6FA',
          boxShadow: 'none',
        },
        '&': {
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            background:
              'linear-gradient(270deg, #F2F6FA 0%, rgba(242, 246, 250, 0.00) 100%)',
            width: '8px',
            opacity: showShadow ? 1 : 0,
            transition: 'opacity 0.3s',
          },
        },
        '& .MuiDataGrid-pinnedColumns div[data-id="total-row-footer"]': {
          position: 'relative',
          '&::after': {
            content: `"${t('planTableTotalFooter.totalText')}"`,
            position: 'absolute',
            right: 4,
            top: 15,
            bottom: 0,
            fontSize: '14px',
            fontWeight: 400,
            color: (theme) => theme.palette.custom.gray2,
          },
        },
      }}
    />
  )
}

export default PlanTableDataCategories
